/**
 * Dashboard sidebar entries — one icon and collapsed initial per board.
 * Version: 1.0 — 2026-09-26
 */
import {
  AccountBookOutlined,
  ApartmentOutlined,
  CalendarOutlined,
  ContainerOutlined,
  IdcardOutlined,
  InboxOutlined,
  LineChartOutlined,
  PieChartOutlined,
  RadarChartOutlined,
  ShopOutlined,
  SwapOutlined,
  TagOutlined,
  FileTextOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type { ComponentType, ReactNode } from 'react';
import type { PortalMenuAccess } from '@/config/portalMenu';

export type DashboardCategory = 'Sales' | 'Products' | 'Inventory';

export type DashboardNavItem = {
  path: string;
  segment: string;
  name: string;
  category: DashboardCategory;
  initials: string;
  access: PortalMenuAccess;
  Icon: ComponentType;
};

export const DASHBOARD_CATEGORY_ORDER: DashboardCategory[] = ['Sales', 'Products', 'Inventory'];

export const DASHBOARD_NAV: DashboardNavItem[] = [
  {
    path: '/dashboard/daily',
    segment: 'daily',
    name: 'Daily Sales',
    category: 'Sales',
    initials: 'DS',
    access: 'canViewDailySalesDashboard',
    Icon: LineChartOutlined,
  },
  {
    path: '/dashboard/sales-summary',
    segment: 'sales-summary',
    name: 'Sales Summary',
    category: 'Sales',
    initials: 'SU',
    access: 'canViewSalesSummary',
    Icon: PieChartOutlined,
  },
  {
    path: '/dashboard/weekly-company-report',
    segment: 'weekly-company-report',
    name: 'Company Sales Report',
    category: 'Sales',
    initials: 'CS',
    access: 'canViewWeeklyCompanyReport',
    Icon: FileTextOutlined,
  },
  {
    path: '/dashboard/membership',
    segment: 'membership',
    name: 'Membership',
    category: 'Sales',
    initials: 'MB',
    access: 'canViewMembershipDashboard',
    Icon: IdcardOutlined,
  },
  {
    path: '/dashboard/single-branch-performance',
    segment: 'single-branch-performance',
    name: 'Single branch performance',
    category: 'Sales',
    initials: '1B',
    access: 'canViewDailySalesDashboard',
    Icon: ShopOutlined,
  },
  {
    path: '/dashboard/branch-performance',
    segment: 'branch-performance',
    name: 'Branch performance',
    category: 'Sales',
    initials: 'BP',
    access: 'canViewDailySalesDashboard',
    Icon: ApartmentOutlined,
  },
  {
    path: '/dashboard/branch-financial-performance',
    segment: 'branch-financial-performance',
    name: 'Branch financial performance',
    category: 'Sales',
    initials: 'FP',
    access: 'canViewBranchFinancialPerformance',
    Icon: AccountBookOutlined,
  },
  {
    path: '/dashboard/promotion-performance',
    segment: 'promotion-performance',
    name: 'Promotion performance',
    category: 'Sales',
    initials: 'PR',
    access: 'canViewPromotionPerformanceDashboard',
    Icon: TagOutlined,
  },
  {
    path: '/dashboard/best-seller-ranking',
    segment: 'best-seller-ranking',
    name: 'Best Seller Ranking',
    category: 'Products',
    initials: 'BR',
    access: 'canViewProductPerformanceDashboard',
    Icon: TrophyOutlined,
  },
  {
    path: '/dashboard/product-360',
    segment: 'product-360',
    name: 'Product 360',
    category: 'Products',
    initials: 'P3',
    access: 'canViewProductPerformanceDashboard',
    Icon: RadarChartOutlined,
  },
  {
    path: '/dashboard/product-stock-health',
    segment: 'product-stock-health',
    name: 'Product Stock Health',
    category: 'Products',
    initials: 'SH',
    access: 'canViewProductPerformanceDashboard',
    Icon: ContainerOutlined,
  },
  {
    path: '/dashboard/sku-weekly-tracker',
    segment: 'sku-weekly-tracker',
    name: 'SKU Weekly Tracker',
    category: 'Products',
    initials: 'WT',
    access: 'canViewProductPerformanceDashboard',
    Icon: CalendarOutlined,
  },
  {
    path: '/dashboard/sku-sales-movement',
    segment: 'sku-sales-movement',
    name: 'SKU Sales Movement',
    category: 'Products',
    initials: 'SM',
    access: 'canViewProductPerformanceDashboard',
    Icon: SwapOutlined,
  },
  {
    path: '/dashboard/inventory',
    segment: 'inventory',
    name: 'Inventory',
    category: 'Inventory',
    initials: 'IN',
    access: 'canViewInventoryDashboard',
    Icon: InboxOutlined,
  },
];

const INITIALS_BY_PATH: Record<string, string> = Object.fromEntries(
  DASHBOARD_NAV.map((item) => [item.path, item.initials]),
);

export const DASHBOARD_CATEGORY_MAP: Record<string, DashboardCategory> = Object.fromEntries(
  DASHBOARD_NAV.map((item) => [item.segment, item.category]),
) as Record<string, DashboardCategory>;

export function dashboardMenuIcon(item: DashboardNavItem): ReactNode {
  const Icon = item.Icon;
  return <Icon />;
}

/** Two-letter mark for the collapsed sider. Dashboard boards use a fixed set so none collide. */
export function collapsedMenuInitials(path?: string, name?: string): string {
  if (path && INITIALS_BY_PATH[path]) {
    return INITIALS_BY_PATH[path];
  }
  const words = (name || '')
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return '•';
}
